/* Angular Component */
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  SimpleChanges,
  ViewChild,
  ViewContainerRef,
} from "@angular/core";
import { MediaMapper } from "../../../infrastructure/repositories/media/media.mapper";
import { UploadService } from "../../../infrastructure/upload/upload-service";
import { MediaInfo } from "../../../domain/media";
import { MediaRepository } from "../../../domain/media-repository";
import { Router } from "@angular/router";
import { GraphicsInfo } from "../../../domain/graphics-info";
import { SearchService } from "../../../services/search.service";
import { Subscription } from "rxjs";
import { GraphicsRepository } from "../../../domain/graphics-repository";
import { AutopilotRepository } from "../../../domain/autopilot-repository";
import { AutopilotRule } from "../../../domain/rule";
import { LibraryTypes } from "../../../domain/enums";
import { LiveFeedRepository } from "../../../domain/live-feed-repository";
import { LiveFeed, LiveFeedInfo } from "../../../domain/live-feed";
import { UI } from "../../../services/ui.service";
import { PopoverService } from "../../../services/popover-service";
import { DynamicPromo, DynamicPromoInfo } from "../../../domain/dyanmicPromo";
import { DynamicPromoRepository } from "../../../domain/dynamic-promo-repository";
import { UserService } from "../../../domain/user-service";
import { Socket } from "../../../infrastructure/socket.service";
import { IUploadFile } from "../../../domain/Upload/i-upload-file";
import { User } from "../../../domain/user";
import { SecEventsService } from "../../calendar/expandable/sec-events.service";
import { SignalStandardType } from "../../../domain/signal-standard-type";
import { Layout } from "../../../domain/layouts/layout";
import { ILayoutsRepository } from "../../../domain/layouts/layouts.repository";
import { IBlockRepository } from "../../../domain/blocks/i-block.repository";
import { NgxSmartModalService } from "ngx-smart-modal";
import { Block } from "../../../domain/blocks/block";
import { IBroadcastsRepository } from "../../../domain/broadcasts/i-broadcasts-repository";
import { BroadcastPreset } from "../../../domain/broadcasts/broadcast-preset";
import { ICommonUtilInterface } from "../../../domain/common/common-util.interface";

const TAG = "LibraryComponent: ";

@Component({
  selector: "library",
  templateUrl: "./library.component.html",
  styleUrls: ["./library.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LibraryComponent {
  @Input()
  viewType = "gallery";

  @Input()
  showItemMenuButtons = true;

  @HostBinding("class.list-view")
  listView: boolean = this.viewType !== "gallery";

  @Input()
  library = [];

  @Input()
  isQCAllowed = false;

  @Input()
  libraryType = "video";

  @ViewChild("items", { read: ViewContainerRef, static: true })
  items: ViewContainerRef;

  newItem: FileList | null = null;
  fileName;
  currentGraphicsTemplateIndex: number;
  currentUser: User;
  isSuperUser = false;
  mediaUploadedFiles: IUploadFile[] = [];

  @Output()
  onScrollChanged: EventEmitter<any> = new EventEmitter<any>();

  graphicUpdatedSubscription: Subscription;
  ruleCreatedSubscription: Subscription;
  liveFeedCreatedSubscription: Subscription;
  liveFeedUpdatedSubscription: Subscription;
  fileIsUploadingSubscription: Subscription;
  addContractUploadFilesSubscription: Subscription;
  createNewMarkerSubscription: Subscription;
  newLayoutCreatedSubscription: Subscription;
  newBlockCreatedSubscription: Subscription;
  newBlockDeletedSubscription: Subscription;
  broadcastDeletedSubscription: Subscription;
  newBroadcastCreatedSubscription: Subscription;
  markersData = [
    {
      type: SignalStandardType.SCTE35,
      duration: 0,
      markerName: "SCTE35",
      outCode: "",
      inCode: "",
      volume: "",
      language: "",
      signalDuration: "",
      signalDelta: "",
      image: "SCTE-sidebar.svg",
    },
    {
      type: SignalStandardType.DTMF,
      duration: 0,
      markerName: "DTMF",
      outCode: "",
      inCode: "",
      volume: "",
      langauge: "",
      signalDuration: "",
      signalDelta: "",
      image: "DTMF-sidebar.svg",
    },
  ];

  constructor(
    private liveFeedRepository: LiveFeedRepository,
    private ref: ElementRef,
    private mediaMapper: MediaMapper,
    private uploadService: UploadService,
    private mediaRepository: MediaRepository,
    private router: Router,
    private _changeDetectorRef: ChangeDetectorRef,
    private searchService: SearchService,
    private graphicsRepository: GraphicsRepository,
    private autopilotRepository: AutopilotRepository,
    private ui: UI,
    private popoverService: PopoverService,
    private dynamicPromoRepository: DynamicPromoRepository,
    private userService: UserService,
    private socketService: Socket,
    private secEventsService: SecEventsService,
    private _layoutsRepository: ILayoutsRepository,
    private _blocksRepository: IBlockRepository,
    private _ngxSmartModalService: NgxSmartModalService,
    private _broadcastPresetsRepository: IBroadcastsRepository,
    private _commonUtils: ICommonUtilInterface,
  ) {}

  getItem(item: DynamicPromo) {
    this.mediaRepository.getMediaInfo(item.mediaId).then((mediaInfo) => {
      this.graphicsRepository
        .getGraphics(item.graphicsId)
        .then((graphicsInfo) => {
          return new DynamicPromoInfo(
            item.id,
            item.name,
            graphicsInfo,
            mediaInfo,
            item.thumbnail,
          );
        });
    });
  }

  openPopover() {
    this.popoverService.open();
  }

  /**
   * Booting up the library
   * @return void
   */
  ngOnInit() {
    this.currentUser = this.userService.getCurrentUser();
    const input = this.ref.nativeElement.querySelector("input");
    const container = this.ref.nativeElement.querySelector(".upload-new-item");
    this.isSuperUser = this.userService.getCurrentUser().isSuperUser;
    this.addContractUploadFilesSubscription =
      this.popoverService.output.subscribe((output) => {
        this.popoverService.close();
        if (output.name === "Upload File") {
          this.onUploadFileChange(event);
        } else if (output.name === "Add Contract") {
          this.mediaRepository.createContract().then((id) => {
            this.router.navigate(["/mam/editMedia", id], {
              queryParamsHandling: "preserve",
            });
          });
        }
      });

    this.fileIsUploadingSubscription =
      this.uploadService.uploadingEvent.subscribe(
        this.onFileIsUploading.bind(this),
      );
    this.graphicUpdatedSubscription =
      this.graphicsRepository.graphicUpdated.subscribe(
        this.onGraphicsLibraryUpdate.bind(this),
      );

    this.ruleCreatedSubscription =
      this.autopilotRepository.ruleCreated.subscribe(
        this.onRuleCreate.bind(this),
      );

    this.liveFeedCreatedSubscription =
      this.liveFeedRepository.liveFeedCreated.subscribe(
        this.onLiveFeedLibraryCreated.bind(this),
      );

    this.liveFeedUpdatedSubscription =
      this.liveFeedRepository.liveFeedUpdated.subscribe(
        this.onLiveFeedLibraryUpdated.bind(this),
      );
    this.newLayoutCreatedSubscription =
      this._layoutsRepository.layoutCreated.subscribe(
        this.onLayoutCreated.bind(this),
      );
    this.newBlockCreatedSubscription = this._blocksRepository.created.subscribe(
      this.onBlockCreated.bind(this),
    );
    this.newBlockDeletedSubscription = this._blocksRepository.deleted.subscribe(
      this.onBlockDeleted.bind(this),
    );

    this.newBroadcastCreatedSubscription =
      this._broadcastPresetsRepository.created.subscribe(
        this.onBroadcastCreated.bind(this),
      );
    this.broadcastDeletedSubscription =
      this._broadcastPresetsRepository.deleted.subscribe(
        this.onBroadcastDeleted.bind(this),
      );

    this.createNewMarkerSubscription =
      this.secEventsService.onCreateNewMarker.subscribe((newMarker) => {
        this.markersData.push({ ...newMarker, image: "markers-icon.svg" });
      });

    /* Validate Items */
    if (input == null || container == null) {
      return;
    }

    /* Kicked when drag enter the dropzone area */
    input.addEventListener(
      "dragenter",
      ((event) => {
        /* Adding Drop-zone Class */
        container.classList.add("library-dropzone");
      }).bind(this),
      false,
    );

    /* Dispatched when drag exit the dropzone area */
    input.addEventListener(
      "dragleave",
      ((event) => {
        /* Removing library drop-zone class */
        container.classList.remove("library-dropzone");
      }).bind(this),
      false,
    );
  }

  ngOnDestroy() {
    if (this.graphicUpdatedSubscription) {
      this.graphicUpdatedSubscription.unsubscribe();
    }
    if (this.liveFeedUpdatedSubscription) {
      this.liveFeedUpdatedSubscription.unsubscribe();
    }
    if (this.liveFeedCreatedSubscription) {
      this.liveFeedCreatedSubscription.unsubscribe();
    }
    if (this.ruleCreatedSubscription) {
      this.ruleCreatedSubscription.unsubscribe();
    }
    if (this.fileIsUploadingSubscription) {
      this.fileIsUploadingSubscription.unsubscribe();
    }
    if (this.addContractUploadFilesSubscription) {
      this.addContractUploadFilesSubscription.unsubscribe();
    }
    if (this.createNewMarkerSubscription) {
      this.createNewMarkerSubscription.unsubscribe();
    }
    if (this.newLayoutCreatedSubscription) {
      this.newLayoutCreatedSubscription.unsubscribe();
    }
    if (this.newBlockCreatedSubscription) {
      this.newBlockCreatedSubscription.unsubscribe();
    }
    if (this.newBlockDeletedSubscription) {
      this.newBlockDeletedSubscription.unsubscribe();
    }

    if (this.broadcastDeletedSubscription) {
      this.broadcastDeletedSubscription.unsubscribe();
    }

    if (this.newBroadcastCreatedSubscription) {
      this.newBroadcastCreatedSubscription.unsubscribe();
    }
  }

  /**
   * dispatched when new graphic rules been added
   * @param graphicsInfo
   */
  onGraphicsLibraryUpdate(graphicsInfo: GraphicsInfo) {
    this.library.unshift(graphicsInfo);
    this._changeDetectorRef.markForCheck();
  }

  onLiveFeedLibraryCreated(liveFeed: LiveFeedInfo) {
    if (this.libraryType !== LibraryTypes.Live) {
      return;
    }
    this.library.unshift(liveFeed);
    this._changeDetectorRef.markForCheck();
  }

  onLayoutCreated(layout: Layout) {
    if (this.libraryType !== LibraryTypes.Layouts) {
      return;
    }
    this.library.unshift(layout);
    this._changeDetectorRef.markForCheck();
  }

  onBlockCreated(block: Block) {
    if (this.libraryType !== LibraryTypes.Blocks) {
      return;
    }
    this.library.unshift(block);
    this._changeDetectorRef.markForCheck();
  }

  onBroadcastCreated(broadcastPreset: BroadcastPreset) {
    if (this.libraryType !== LibraryTypes.Broadcasts) {
      return;
    }
    this.library.unshift(broadcastPreset);
    this._changeDetectorRef.markForCheck();
  }

  onBlockDeleted(block: Block) {
    if (this.libraryType !== LibraryTypes.Blocks) {
      return;
    }
    this.library.splice(this.library.indexOf(block), 1);
    this._changeDetectorRef.markForCheck();
  }

  onBroadcastDeleted(broadcastPreset: BroadcastPreset) {
    if (this.libraryType !== LibraryTypes.Broadcasts) {
      return;
    }
    this.library.splice(this.library.indexOf(broadcastPreset), 1);
    this._changeDetectorRef.markForCheck();
  }

  onLiveFeedLibraryUpdated(liveFeed: LiveFeedInfo) {
    if (this.libraryType !== LibraryTypes.Live) {
      return;
    }

    const index = this.library.findIndex((x) => x.id === liveFeed.id);
    if (index > -1) {
      this.library[index] = liveFeed;
    }
  }

  onFileIsUploading() {
    this._changeDetectorRef.markForCheck();
  }

  /**
   * add new rule as GraphicsRule
   * @param rule
   */
  onRuleCreate(rule: AutopilotRule) {
    if (this.libraryType !== LibraryTypes.AutopilotRules) {
      return;
    }

    this.library.unshift(rule);
    this._changeDetectorRef.markForCheck();

    console.log("THIS IS THE ONE!", rule);
  }

  /**
   * Detect Changes and performing actions according to it
   * @param changes
   * @return void
   */
  ngOnChanges(changes: SimpleChanges) {
    if (changes["viewType"]) {
      this.updateViewMode(changes["viewType"].currentValue);
    }
  }

  /**
   *
   * @param newMode
   */
  updateViewMode(newMode: string) {
    this.listView = newMode !== "gallery";
  }

  getMediaUploadedFiles() {
    if (
      this.searchService.statusChosen === "All" ||
      this.searchService.statusChosen === "Uploading"
    ) {
      return (this.mediaUploadedFiles =
        this.uploadService.getUploadedMediaFiles());
    }
    const uploadedFiles: IUploadFile[] = [];
    return uploadedFiles;
  }

  /**
   * Navigate to create new rule screen
   * @return void
   */
  addNewRule() {
    /**
     * hacky! remove after we move to Angular router 5 and above.
     * use onSameUrlNavigation when initiating routes
     * https://angular.io/api/router/ExtraOptions
     */

    this.router
      .navigateByUrl("/autopilot/graphics/reload", { skipLocationChange: true })
      .then(() => this.router.navigate(["/autopilot/new"]));
  }

  addNewFeed() {
    this.router.navigate(["/scheduler/live"]);
  }

  addNewDynamicPromo() {
    this.router.navigate(["/scheduler/dynamicPromo"]);
  }

  async onUploadFileChange(event) {
    const uploadInstructions = await this.createUploadInstructionList(
      event.target.files,
    );
    for (let i = 0; i < uploadInstructions.length; i++) {
      const instruction = uploadInstructions[i];

      this.mediaUploadedFiles.push(
        this.uploadService.uploadMedia(instruction.mediaFile, null, {
          DuplicateIdHandlingStrategy: instruction.replaceIdStrategy,
        }),
      );
    }
    event.target.value = "";
  }

  onRemoveUploadedMedia(mediaId: number, uploadFile: IUploadFile) {
    const index = this.mediaUploadedFiles.indexOf(uploadFile);
    if (index > -1) {
      this.mediaUploadedFiles.splice(index, 1);
    }

    if (mediaId) {
      this.mediaRepository.deleteMedia(mediaId);
    }
  }

  onMediaUploadSuccess(mediaInfo: MediaInfo, uploadFile: IUploadFile) {
    const index = this.mediaUploadedFiles.indexOf(uploadFile);
    if (index > -1) {
      this.mediaUploadedFiles.splice(index, 1);
    }
    if (this.libraryType === "video") {
      this.library.unshift(mediaInfo);
    }
  }

  getBackgroundImage(item) {
    return item.ContentSnapshotUrl;
  }

  trackById(index, item) {
    return item.id;
  }

  onInstancesClick(templateIndex) {
    if (templateIndex === this.currentGraphicsTemplateIndex) {
      return (this.currentGraphicsTemplateIndex = null);
    }

    this.currentGraphicsTemplateIndex = templateIndex;
  }

  getGraphicsTemplates() {
    return this.library.filter((x: GraphicsInfo) => x.isTemplate);
  }

  getCurrentGraphicsTemplate() {
    return Number.isInteger(this.currentGraphicsTemplateIndex)
      ? this.getGraphicsTemplates()[this.currentGraphicsTemplateIndex]
      : null;
  }

  getCurrentGraphicsInstances() {
    const template = this.getCurrentGraphicsTemplate();
    if (!template) {
      return [];
    }
    return this.library.filter(
      (x: GraphicsInfo) => x.instanceTemplateId === template.id,
    );
  }

  onScroll() {
    this.onScrollChanged.emit();
  }

  onDeleteAutopilotRule(rule: AutopilotRule, ruleIndex) {
    this.autopilotRepository
      .markDeleted(rule.id)
      .then(() => {
        this.library.splice(ruleIndex, 1);
        this._changeDetectorRef.markForCheck();
      })
      .catch(() => {});
  }

  onDeleteLiveFeed(liveFeed: LiveFeed, index) {
    this.liveFeedRepository
      .deleteLiveFeed(liveFeed.id)
      .then(() => {
        if (this.libraryType !== LibraryTypes.Live) {
          return;
        }
        this.library.splice(index, 1);
        this._changeDetectorRef.markForCheck();
      })
      .catch((err) => {
        const message =
          err.status === 400 ? err.error.message : "An error occurred";
        this.ui.notify(this.ui.EVENTS.ShowAlert, {
          title: "Delete",
          message: message,
        });
      });
  }

  deleteDynamicPromo(dynamicPromo: DynamicPromo, index) {
    this.dynamicPromoRepository
      .deleteDynamicPromo(dynamicPromo.id)
      .then(() => {
        if (this.libraryType !== LibraryTypes.DynamicPromo) {
          return;
        }
        this.library.splice(index, 1);
        this._changeDetectorRef.markForCheck();
      })
      .catch((err) => {
        const message =
          err.status === 400
            ? "Dynamic promo is scheduled and cannot be deleted"
            : "An error occurred";
        this.ui.notify(this.ui.EVENTS.ShowAlert, {
          title: "Delete",
          message: message,
        });
      });
  }

  deleteLayout(layout: Layout, index) {
    this._layoutsRepository
      .delete(layout.id)
      .then(() => {
        if (this.libraryType !== LibraryTypes.Layouts) {
          return;
        }
        this.library.splice(index, 1);
        this._changeDetectorRef.markForCheck();
      })
      .catch((err) => {
        const message =
          err.status === 400
            ? "Layout is scheduled and cannot be deleted"
            : "An error occurred";
        this.ui.notify(this.ui.EVENTS.ShowAlert, {
          title: "Delete",
          message: message,
        });
      });
  }
  deleteBlock(blockId: number, index) {
    this._blocksRepository
      .delete(blockId)
      .then(() => {
        if (this.libraryType !== LibraryTypes.Blocks) {
          return;
        }
        //this.library.splice(index, 1);
        this._changeDetectorRef.markForCheck();
      })
      .catch((err) => {
        const message = err?.message || "An error occurred";
        this.ui.notify(this.ui.EVENTS.ShowAlert, {
          title: "Delete",
          message: message,
        });
      });
  }

  onDestroy() {}

  private createUploadInstructionList(
    fileList: FileList,
  ): Promise<UploadMediaItemInstruction[]> {
    return new Promise<UploadMediaItemInstruction[]>(async (resolve) => {
      const existingIds: UploadMediaItemInstruction[] = [];
      const newIds: UploadMediaItemInstruction[] = [];

      for (let i = 0; i < fileList.length; i++) {
        const file = fileList[i];
        const fileNameSplitted = file.name.split(".");
        fileNameSplitted.pop();
        const tenantMediaId = fileNameSplitted.join(".");

        const existingMediaId =
          await this.mediaRepository.getMediaIdByTenantUniqueId(tenantMediaId);
        console.log(
          "createUploadInstructionList->existingMediaId",
          existingMediaId,
        );
        if (existingMediaId > 0) {
          existingIds.push(new UploadMediaItemInstruction(file, tenantMediaId));
        } else {
          newIds.push(new UploadMediaItemInstruction(file, tenantMediaId));
        }
      }

      if (existingIds.length > 0) {
        this.ui.notify(this.ui.EVENTS.ShowConfirm, {
          title: "Existing Files",
          message:
            "The following files exist in MAM: " +
            existingIds.map((inst) => inst.tenantMediaId).join(" , "),
          buttons: [
            {
              title: "Skip",
              callback: () => {
                //remove all existing instructions
                existingIds.splice(0);
                resolve(existingIds.concat(newIds));
              },
            },
            {
              title: "Replace",
              callback: () => {
                existingIds.forEach(
                  (instruction) =>
                    (instruction.replaceIdStrategy =
                      EReplaceIdStrategy.ReplaceFiles),
                );
                resolve(existingIds.concat(newIds));
              },
            },
            {
              title: "OK",
              callback: () => {
                //don't do nothing
                resolve(existingIds.concat(newIds));
              },
            },
          ],
        });
      } else {
        resolve(existingIds.concat(newIds));
      }
    });
  }

  addNewLayout() {
    this.router.navigate(["/layouts/new"]).then();
  }

  addNewBroadcast() {
    const newBroadcastPreset = BroadcastPreset.createNew(
      "Untitled",
      this._commonUtils,
    );
    this._broadcastPresetsRepository.create(newBroadcastPreset).then(() => {
      return this.router.navigate(["/broadcasts/edit/", newBroadcastPreset.id]);
    });
  }
  addNewBlock() {
    this._ngxSmartModalService.getModal("createBlockModal").open();
    //this.router.navigate(['/blocks/new']).then();
  }

  onDeleteBroadcastPreset(broadcastPreset: any, index: number) {
    this._broadcastPresetsRepository
      .delete(broadcastPreset.id)
      .then(() => {
        if (this.libraryType !== LibraryTypes.Live) {
          return;
        }
        this.library.splice(index, 1);
        this._changeDetectorRef.markForCheck();
      })
      .catch((err) => {
        const message =
          err.status === 400 ? err.error.message : "An error occurred";
        this.ui.notify(this.ui.EVENTS.ShowAlert, {
          title: "Delete",
          message: message,
        });
      });
  }
}

export enum EReplaceIdStrategy {
  StealId,
  ReplaceFiles,
}

class UploadMediaItemInstruction {
  constructor(
    public mediaFile: File,
    public tenantMediaId: string,
    public replaceIdStrategy: EReplaceIdStrategy = EReplaceIdStrategy.StealId,
  ) {}
}
